import Button from '@components/dataEntry/button/Button';
import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { LevelSystem, PetTemplate, UserPet } from '@typings/pets/pet.interface';
import { manualEvolve, ManualEvolveParams } from '@pages/Pets/utils/manualEvolve';
import { CollectionPath } from '@typings/collections.enum';
import { currentUserIDState } from '@atom/setting/user';
import { useRecoilValue } from 'recoil';
import { useDocument } from '@nandorojo/swr-firestore';


type LevelUpReward = {
  id?: string;
  name?: string;
  iconURL?: string;
  amount?: number;
};

type Props = {
  info?: { from: number; to: number } | null;
  onClose: () => void;
  rewards?: LevelUpReward[]; // 외부에서 직접 전달 (옵션)
  levelSystem?: LevelSystem | null; // 내부 조립용 (옵션)
  canEvolve?: boolean; // (호환용) 외부 전달값, 내부 계산과 OR 처리
  activePet: UserPet;
  isOpen: boolean; // 항상 마운트, 열림 제어
};

const LevelUpModal: React.FC<Props> = ({ info, onClose, rewards = [], levelSystem, canEvolve, activePet, isOpen }) => {

  // 진화 옵션 불러오기
  const currentUid = useRecoilValue(currentUserIDState);
  const activePetId = activePet.id;
  const petDocPath =
    currentUid && activePetId
      ? `${CollectionPath.USERS}/${currentUid}/pets/${activePetId}`
      : '';
  const currentLevel = Number(activePet?.level || 1);
  const levelKey = useMemo(() => (currentLevel ? `level${currentLevel}` : ''), [currentLevel]);

  const { data: currentTemplateDoc } = useDocument<PetTemplate>(
    activePet?.petTemplateId ? `${CollectionPath.PET}/${activePet.petTemplateId}` : null,
    { listen: true },
  );
  const { data: userPetDoc } = useDocument<any>(
    currentUid && activePetId ? `${CollectionPath.USERS}/${currentUid}/pets/${activePetId}` : null,
    { listen: true },
  );
  const evolutionOptions = useMemo(() => {
    const trees: any = (currentTemplateDoc as any)?.evolutionTrees;
    if (!trees || !levelKey) return [] as any[];
    const options = trees[levelKey];
    return Array.isArray(options) ? options : [];
  }, [currentTemplateDoc, levelKey]);

  // 내부 진화 가능 판단: 현재 레벨에서 진화 옵션이 존재하고 requiredLevel 충족
  const eligibleForEvolve = useMemo(() => {
    if (!Array.isArray(evolutionOptions) || evolutionOptions.length === 0) return false;
    return evolutionOptions.some((opt: any) => Number(opt?.requiredLevel || currentLevel) <= currentLevel && !!opt?.imgURL);
  }, [evolutionOptions, currentLevel]);

  // 레벨업 보상 조립
  // 표시 레벨: 외부 info가 있으면 그 to, 없으면 현재 펫 레벨
  const displayLevel = Number((info as any)?.to || currentLevel || 1);

  const computedRewards = useMemo<LevelUpReward[]>(() => {
    if (Array.isArray(rewards) && rewards.length > 0) return rewards;
    if (!levelSystem || !displayLevel) return [];
    const lvl = Number(displayLevel);
    const rule: any = (levelSystem as any)?.levelUpRewards?.[lvl];
    if (!rule) return [];
    const list: LevelUpReward[] = [];
    if (typeof rule.currency === 'number' && rule.currency > 0) {
      list.push({ id: `currency_${lvl}`, name: '젬', amount: rule.currency });
    }
    if (Array.isArray(rule.items)) {
      rule.items.forEach((itemId: string, idx: number) => {
        list.push({ id: `item_${itemId}_${idx}`, name: itemId, amount: 1 });
      });
    }
    if (Array.isArray(rule.unlocks)) {
      rule.unlocks.forEach((name: string, idx: number) => {
        list.push({ id: `unlock_${idx}`, name: `해금: ${name}` });
      });
    }
    return list;
  }, [rewards, levelSystem, displayLevel]);



  return (
    <LevelUpOverlay $open={isOpen} onClick={onClose}>
      <LevelUpCard onClick={(e) => { e.stopPropagation(); onClose(); }}>
        <LevelUpRing />
        <Badge>LEVEL UP!</Badge>

        <CenterWrap>
          <Emblem>
            <EmblemGlow />
            <EmblemIcon>🐾</EmblemIcon>
          </Emblem>
        </CenterWrap>
        <LevelNumber>{displayLevel || 1}</LevelNumber>
        <LevelUpSub>레벨업 완료!</LevelUpSub>

        {computedRewards && computedRewards.length > 0 ? (
          <RewardsWrap role="list" aria-label="획득 보상">
            <RewardsTitle>획득 보상</RewardsTitle>
            <RewardsRow>
              {computedRewards.map((r, i) => (
                <RewardBox key={(r.id || r.name || 'reward') + i} role="listitem">
                  <RewardThumb>
                    {r.iconURL ? (
                      <img src={r.iconURL} alt={r.name || 'reward'} />
                    ) : (
                      <span>💎</span>
                    )}
                  </RewardThumb>
                  {r.amount != null && <RewardAmount>{r.amount}</RewardAmount>}
                </RewardBox>
              ))}
            </RewardsRow>
          </RewardsWrap>
        ) : null}

        <LevelUpHint>화면을 터치하면 닫혀요</LevelUpHint>
        {(() => {
          const showEvolveBtn = Boolean(canEvolve) || eligibleForEvolve;
          return showEvolveBtn ? (
            <Button size="sm" onClick={async (e) => {
              e.stopPropagation();

              try {
                const res = await manualEvolve({
                  activePet,
                  currentUid: String(currentUid || ''),
                  petDocPath,
                  evolutionOptions,
                  levelKey,
                } as any);
                if (!res.ok && res.reason === 'no_options') alert('진화 가능한 옵션이 없습니다.');
              } catch (err) {
                alert('진화 적용에 실패했습니다.');
              }
            }}>
              진화하기
            </Button>
          ) : null;
        })()}
        {Array.from({ length: 28 }).map((_, i) => {
          const deg = Math.round((360 / 28) * i);
          const delay = (i % 7) * 35;
          const colors = [
            '#FF5252',
            '#FFEB3B',
            '#69F0AE',
            '#40C4FF',
            '#7C4DFF',
          ];
          const color = colors[i % colors.length];
          return (
            <Confetti
              key={`cf_${i}`}
              style={{
                ['--deg' as any]: `${deg}deg`,
                ['--delay' as any]: `${delay}ms`,
                ['--color' as any]: color,
              }}
            />
          );
        })}
      </LevelUpCard>
    </LevelUpOverlay>
  );
};

export default LevelUpModal;

// styles (기존 PetView.tsx의 레벨업 모달 스타일 분리)
const fadeIn = keyframes`
  from { opacity: 0 }
  to { opacity: 1 }
`;
const popIn = keyframes`
  0% { transform: scale(0.8); opacity: 0 }
  60% { transform: scale(1.05); opacity: 1 }
  100% { transform: scale(1) }
`;

const LevelUpOverlay = styled.div<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ $open }) => ($open ? 10 : -1)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 0.2s ease;
  animation: ${fadeIn} 0.2s ease;
`;

const LevelUpCard = styled.div`
  position: relative;
  min-width: 300px;
  padding: 24px 24px 18px 24px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--cardBackgroundColor);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
  text-align: center;
  animation: ${popIn} 280ms ease;
`;
const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  font-weight: 900;
  letter-spacing: 0.5px;
  background: linear-gradient(90deg, #ffeb3b, #ff9800, #ffeb3b);
  color: #3b2a00;
`;


const CenterWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin: 8px 0 6px 0;
`;

const Emblem = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 45%, #fff7d6, #ffd54f);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15), inset 0 2px 8px rgba(255,255,255,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmblemGlow = styled.div`
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 215, 0, 0.35), rgba(255, 215, 0, 0));
  filter: blur(2px);
`;

const EmblemIcon = styled.div`
  font-size: 36px;
`;

const LevelNumber = styled.div`
  margin-top: 8px;
  font-size: 44px;
  font-weight: 900;
  color: #ffb300;
  text-shadow: 0 2px 0 rgba(0,0,0,0.1), 0 6px 16px rgba(255, 193, 7, 0.65);
`;

const LevelUpSub = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: var(--subTextColor);
`;

const LevelUpHint = styled.div`
  font-size: 11px;
  color: var(--subTextColor);
`;

const LevelUpRing = styled.div`
  position: absolute;
  inset: -12px;
  border-radius: 20px;
  pointer-events: none;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(255, 215, 0, 0.2),
    rgba(255, 215, 0, 0) 60%
  );
  filter: blur(2px);
`;

const confettiBurst = keyframes`
  0% {
    opacity: 1;
    transform: translate(-50%, -50%) rotate(var(--deg)) translateY(0) scale(0.8);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(var(--deg)) translateY(-120px) scale(1);
  }
`;

const Confetti = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color);
  transform: translate(-50%, -50%);
  opacity: 0;
  animation: ${confettiBurst} 700ms ease-out forwards;
  animation-delay: var(--delay, 0ms);
`;

// Rewards
const RewardsWrap = styled.div`
  margin-top: 10px;
  border-top: 1px solid var(--cardLineColor);
  border-bottom: 1px solid var(--cardLineColor);
  padding: 10px 12px;
`;

const RewardsTitle = styled.div`
  font-size: 12px;
  color: var(--subTextColor);
  margin-bottom: 8px;
`;

const RewardsRow = styled.div`
display: flex;
align-items: center;
justify-content: center;
`;

const RewardBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px;
  background: rgba(0,0,0,0.4);
  width: 52px;
  height: 52px;
  border-radius: 8px;
  border: 1px solid #ffd54f;
  box-shadow: 0 0 0 rgba(0,0,0,0.1), 0 6px 16px rgba(255, 193, 7, 0.65);
`;

const RewardThumb = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: var(--inkColor);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; display: block; }
`;
const RewardAmount = styled.div`
  font-weight: 800;
  font-size: 12px;
  color: var(--primaryColor);
`;
